/**
 * Fetch-on-mount with the three states a screen actually has.
 *
 * Deliberately not a data library. There are five endpoints, none of them
 * mutate anything, and the cache invalidation problem a library solves does
 * not exist here.
 *
 * The one thing it must get right is ordering: a reader typing in the search
 * box fires a request per keystroke and they do not come back in order. A
 * slow early response landing after a fast later one puts the wrong results
 * on screen, and nothing about the page looks wrong. Each run is stamped and
 * a stale one is dropped.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export type Async<T> = {
  data: T | null;
  error: Error | null;
  loading: boolean;
  reload: () => void;
};

export function useAsync<T>(run: () => Promise<T>, deps: unknown[]): Async<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const latest = useRef(0);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    const mine = ++latest.current;
    setLoading(true);
    run()
      .then((value) => {
        if (mine !== latest.current) return;
        setData(value);
        setError(null);
      })
      .catch((problem: Error) => {
        if (mine !== latest.current) return;
        setError(problem);
      })
      .finally(() => {
        if (mine === latest.current) setLoading(false);
      });
    // `run` is a fresh closure every render, so it cannot be a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, attempt]);

  return { data, error, loading, reload };
}

/** Wait for typing to stop before asking the server. */
export function useDebounced<T>(value: T, ms = 250): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}
